import { omit, pick, pipe, set, update } from "lodash/fp";
import { useToast } from "primevue/usetoast";
import { Ref } from "vue";

import { contactInfo, moveIn, moveInInspections, students } from "@/api";
import { stripPhoneNumber } from "@/utilities";
import { useForm } from "@/utilities/validations";
import { EmergencyContact } from "@/views/ContactInfo/components/ContactInfoForm/types";

import { InspectionForm } from "./components/InspectionStep/types";
import { MoveInWizardForm, Step } from "./types";
import axios from "axios";

export function useSubmission(
  studentId: number,
  form: Ref<MoveInWizardForm>,
  step: Ref<Step>,
  next: () => void,
  loading: { general: boolean; initial: boolean },
  fetchData: (step: Step) => Promise<void>
) {
  const toast = useToast();
  const formName = "move-in-form";
  const { submit: handleSubmit } = useForm(formName);

  const nullOutEmptyPhone =
    (phoneField: "phone1" | "phone2" | "phone3") =>
    (contact: EmergencyContact) => {
      if (contact[phoneField] == null || contact[phoneField].trim().length === 0) {
        return pipe(
          set(phoneField, null),
          set(phoneField + "Type", null)
        )(contact);
      }

      return contact;
    };

  const santatizeContactInfo = pipe(
    pick(["contact1", "contact2"]),
    (data) =>
      data.contact2.contactName
        ? pipe(
            update("contact2.phone1", stripPhoneNumber),
            update("contact2.phone2", stripPhoneNumber),
            update("contact2.phone3", stripPhoneNumber),
            update("contact2", nullOutEmptyPhone("phone1")),
            update("contact2", nullOutEmptyPhone("phone2")),
            update("contact2", nullOutEmptyPhone("phone3"))
          )(data)
        : omit(["contact2"])(data),
    update("contact1.phone1", stripPhoneNumber),
    update("contact1.phone2", stripPhoneNumber),
    update("contact1.phone3", stripPhoneNumber),
    update("contact1", nullOutEmptyPhone("phone1")),
    update("contact1", nullOutEmptyPhone("phone2")),
    update("contact1", nullOutEmptyPhone("phone3"))
  );

  const sanatizeStudentInfo = pipe(
    pick(["email2", "cellphone", "smsPermission"]),
    update("cellphone", stripPhoneNumber)
  );

  const submit = handleSubmit(async () => {
    try {
      switch (step.value.name) {
        case "Contact":
          await Promise.all([
            contactInfo.update({
              params: {
                id: studentId,
              },
              data: santatizeContactInfo(form.value),
            }),
            students.update({
              params: {
                id: studentId,
              },
              data: sanatizeStudentInfo(form.value),
            }),
          ]);
          form.value = null;
          break;
        case "Inspection":
          await moveInInspections.create({
            data: {
              inspection: {
                studentSignatureConfirmation: (form.value as InspectionForm)
                  .signature,
                inspectionDetailsAttributes: (form.value as InspectionForm)
                  .details,
              },
            },
          });
          form.value = null;
          break;
        case "MoveIn":
          await moveIn.create({
            data: {
              moveInForm: form.value,
            },
          });
          break;
      }
      step.value.completed = true;
      loading.general = true;
      next();
      await fetchData(step.value);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response) {
        const { data } = err.response;

        if (data.errors) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: data.errors["base"]?.join("<br>"),
            life: 3000,
          });
        }
      }
    } finally {
      loading.general = false;
    }
  });

  return { submit, formName, form };
}
